import { Config } from '@/bootstrap/config';
import { ImageTile } from 'ol';
import { getWidth } from 'ol/extent';
import * as source from 'ol/source';
import TileGrid from 'ol/tilegrid/TileGrid';
import WMTS from 'ol/tilegrid/WMTS';
import { addProjections, EPSG25833 } from './Projections';

/**
 * User-selectable map layer types
 */
export enum MapLayerType {
    STANDARD = 'standard',
    FLYFOTO = 'flyfoto',
    GRAATONE = 'graatone',
    TOPOGRAFISK = 'topografisk',
    KANVAS = 'kanvas',
    KANVAS_MORK = 'kanvas_mork',
    BASIS = 'basis',
    BASIS_GRAATONE = 'basis_graatone',
    OPENSTREETMAP = 'openstreetmap',
}

/**
 * Internal tile sources that are actually rendered
 */
export enum TileSourceType {
    GEODATA = 'GeocacheTrafikkJPG',
    TOPO = 'topo',
    TOPOGRAATONE = 'topograatone',
    NIB = 'nib_utm33_wmts_v2',
    KANVAS = 'GeocacheKanvas',
    KANVAS_DARK = 'GeocacheKanvasMork',
    BASIS = 'GeocacheBasis',
    BASIS_GRAY = 'GeocacheGraatone',
    OPENSTREETMAP = 'openstreetmap',
}

export const mapLayerToCanvasMode: { [key in MapLayerType]: 'dark' | 'white' | 'gray' } =
    {
        [MapLayerType.STANDARD]: 'white',
        [MapLayerType.FLYFOTO]: 'white',
        [MapLayerType.GRAATONE]: 'gray',
        [MapLayerType.TOPOGRAFISK]: 'white',
        [MapLayerType.KANVAS]: 'white',
        [MapLayerType.KANVAS_MORK]: 'dark',
        [MapLayerType.BASIS]: 'white',
        [MapLayerType.BASIS_GRAATONE]: 'gray',
        [MapLayerType.OPENSTREETMAP]: 'white',
    };

addProjections();
const projectionExtent = EPSG25833.getExtent();
const size = getWidth(projectionExtent) / 256;
const levels = 19;
export const resolutions = getResolutions(levels, size);
export const matrixIds = getMatrixIds(levels);

function getResolutions(zoomLevels: number, tileSize: number) {
    return new Array(zoomLevels).fill(0).map((val, idx) => tileSize / Math.pow(2, idx));
}

function getMatrixIds(zoomLevels: number): string[] {
    return new Array(zoomLevels).fill(0).map((val, idx) => idx.toString());
}

export const kanvasResolutions = [
    10837.35500804335, 5418.677504021675, 2709.3387520108377, 1354.6693760054188,
    677.3346880027094, 338.6673440013547, 169.33367200067735, 84.66683600033868,
    42.33341800016934, 21.16670900008467, 10.583354500042335, 5.291677250021167,
    2.6458386250105836, 1.3229193125052918, 0.6614596562526459, 0.33072982812632296,
];

export const geodataResolutions = [
    21674.7100160867, 10837.35500804335, 5418.677504021675, 2709.3387520108377,
    1354.6693760054188, 677.3346880027094, 338.6673440013547, 169.33367200067735,
    84.66683600033868, 42.33341800016934, 21.16670900008467, 10.583354500042335,
    5.291677250021167, 2.6458386250105836, 1.3229193125052918, 0.6614596562526459,
    0.33072982812632296, 0.16536491406316148, 0.08268245703158074,
];

export const maxZoomBaseLayer = 14;

/**
 * Determines which tile source to use based on the selected map layer and current zoom level.
 * At high zoom levels (> maxZoomBaseLayer), certain map types use TOPO4 or GRAYSCALE instead.
 */
export function resolveTileSource(
    selectedMap: MapLayerType,
    zoom: number,
    includeTopoWithStandardMap: boolean = false
): TileSourceType {
    const isHighZoom = zoom > maxZoomBaseLayer;

    switch (selectedMap) {
        case MapLayerType.STANDARD:
            return isHighZoom && includeTopoWithStandardMap
                ? TileSourceType.TOPO
                : TileSourceType.GEODATA;
        case MapLayerType.KANVAS:
            return isHighZoom ? TileSourceType.TOPO : TileSourceType.KANVAS;
        case MapLayerType.KANVAS_MORK:
            return isHighZoom ? TileSourceType.TOPOGRAATONE : TileSourceType.KANVAS_DARK;
        case MapLayerType.BASIS:
            return isHighZoom ? TileSourceType.TOPO : TileSourceType.BASIS;
        case MapLayerType.BASIS_GRAATONE:
            return isHighZoom ? TileSourceType.TOPOGRAATONE : TileSourceType.BASIS_GRAY;
        case MapLayerType.FLYFOTO:
            return TileSourceType.NIB;
        case MapLayerType.GRAATONE:
            return TileSourceType.TOPOGRAATONE;
        case MapLayerType.TOPOGRAFISK:
            return TileSourceType.TOPO;
        case MapLayerType.OPENSTREETMAP:
            return TileSourceType.OPENSTREETMAP;
        default:
            return TileSourceType.GEODATA;
    }
}

export const createOpenStreetMapSource = (config: Config): source.XYZ => {
    return new source.XYZ({
        crossOrigin: 'anonymous',
        url: config.url_openstreetmap ?? undefined,
        maxZoom: 19,
        attributions:
            '© <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
    });
};

export const createGeodataSource = (config: Config): source.XYZ => {
    return new source.XYZ({
        crossOrigin: 'Anonymous',
        projection: EPSG25833,
        tileGrid: new TileGrid({
            extent: projectionExtent,
            origin: [-2500000, 9045984],
            resolutions: geodataResolutions,
        }),
        url: config.url_basemap,
        attributions:
            '© NVDB, Geovekst, kommunene og Open Street Map contributors (utenfor Norge)',
    });
};

export const createNiBLayerSource = (config: Config): source.WMTS => {
    /* NiB layer WMTS does not support zoom levels greater than 17.
     */
    const nibLevels = 18;
    return new source.WMTS({
        crossOrigin: 'Anonymous',
        layer: config.nib_layer,
        matrixSet: config.nib_matrixset,
        projection: EPSG25833,
        style: config.nib_style,
        format: 'image/jpgpng',
        tileGrid: new WMTS({
            extent: EPSG25833.getExtent(),
            matrixIds: getMatrixIds(nibLevels),
            origin: [-2500000, 9045984],
            resolutions: getResolutions(nibLevels, size),
        }),
        tileLoadFunction: (tile, url) => {
            if (!(tile instanceof ImageTile)) return;
            const image = tile.getImage() as HTMLImageElement;
            image.src = `${url}&token=${config.nib_token}`;
        },
        urls: config.url_nib,
        attributions: '© Kartverket, Geovekst og kommunene',
    });
};

export const createTopoLayerSource = (config: Config): source.WMTS => {
    return new source.WMTS({
        format: 'png',
        crossOrigin: 'Anonymous',
        layer: config.topo_layer,
        matrixSet: config.topo_matrixset,
        projection: config.topo_projection,
        style: config.topo_style,
        tileGrid: new WMTS({
            extent: EPSG25833.getExtent(),
            matrixIds: matrixIds.map(id => `${id}`),
            origin: [-2500000, 9045984],
            resolutions,
        }),
        urls: config.url_topo,
        attributions: '© Kartverket',
    });
};

export const createGrayscaleLayerSource = (config: Config): source.WMTS => {
    return new source.WMTS({
        format: 'png',
        crossOrigin: 'Anonymous',
        layer: 'topograatone',
        matrixSet: 'utm33n',
        projection: 'EPSG:25833',
        style: 'default',
        tileGrid: new WMTS({
            extent: EPSG25833.getExtent(),
            matrixIds: matrixIds.map(id => `${id}`),
            origin: [-2500000, 9045984],
            resolutions,
        }),
        urls: config.url_topo,
        attributions: '© Kartverket',
    });
};

export const createPropertyBoundariesLayerSource = (config: Config): source.ImageWMS => {
    return new source.ImageWMS({
        crossOrigin: 'Anonymous',
        url: config.url_matrikkelen ?? undefined,
        projection: EPSG25833,
        params: { LAYERS: 'matrikkelkart' },
    });
};
